import React, { useState, useEffect, useRef, useCallback } from "react";

interface StreamingTextBoxProps {
  text?: string;
  speed?: number;
  autoStart?: boolean;
}

const StreamingTextBox = ({
  text,
  showCursor = true,
}: {
  text: string;
  showCursor?: boolean;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  // Auto-scroll effect
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [text]);
  return (
    <div style={{ display: "inline" }}>
      <div
        ref={contentRef}
        style={{
          lineHeight: "1.6",
          color: "#555",
          fontSize: "16px",
          minHeight: "24px",
          scrollBehavior: "smooth",
          wordBreak: "break-word",
          whiteSpace: "pre-line",
          display: "inline",
        }}
      >
        {text}
        {showCursor && (
          <span
            style={{
              display: "inline-block",
              width: "2px",
              height: "20px",
              background: "#667eea",
              marginLeft: "2px",
              animation: "blink 1s infinite",
              verticalAlign: "text-bottom",
            }}
          />
        )}
      </div>
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export const StreamingTextBoxPlayer = ({
  text = `Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum. Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.`,
  speed = 50,
  autoStart = true,
}: StreamingTextBoxProps) => {
  const [displayedText, setDisplayedText] = useState<string>("");
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [showCursor, setShowCursor] = useState<boolean>(true);
  const [buttonHovers, setButtonHovers] = useState<{ [key: string]: boolean }>({
    start: false,
    pause: false,
    reset: false,
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const stopStreaming = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsStreaming(false);
    setIsPaused(false);
    setShowCursor(false);
  }, []);

  const startStreaming = useCallback(() => {
    if (isPaused) {
      setIsPaused(false);
    } else if (currentIndex >= text.length) {
      // Reset if we've finished
      setCurrentIndex(0);
      setDisplayedText("");
    }

    setIsStreaming(true);
    setShowCursor(true);

    intervalRef.current = setInterval(
      () => {
        setCurrentIndex((prevIndex) => {
          if (prevIndex >= text.length) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            setIsStreaming(false);
            setShowCursor(false);
            return prevIndex;
          }

          const nextChar = text[prevIndex];
          setDisplayedText((prev) => prev + nextChar);

          return prevIndex + 1;
        });
      },
      speed + Math.random() * 30,
    ); // Variable speed for realism
  }, [text, speed, isPaused, currentIndex]);

  const pauseStreaming = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsStreaming(false);
    setIsPaused(true);
    setShowCursor(true);
  }, []);

  const resetText = useCallback(() => {
    stopStreaming();
    setCurrentIndex(0);
    setDisplayedText("");
    setShowCursor(false);
  }, [stopStreaming]);

  // Auto-start effect
  useEffect(() => {
    if (autoStart) {
      const timer = setTimeout(() => {
        startStreaming();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [autoStart, startStreaming]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Auto-scroll effect
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [displayedText]);

  const getButtonText = (): string => {
    if (isPaused) return "Resume";
    if (isStreaming) return "Streaming...";
    if (currentIndex >= text.length && displayedText) return "Start Again";
    return "Start Streaming";
  };

  const handleMouseEnter = (button: string) => {
    setButtonHovers((prev) => ({ ...prev, [button]: true }));
  };

  const handleMouseLeave = (button: string) => {
    setButtonHovers((prev) => ({ ...prev, [button]: false }));
  };

  return (
    <div
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: "#f5f5f5",
        padding: "40px",
        minHeight: "100vh",
        margin: 0,
      }}
    >
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            background: "white",
            border: "1px solid #e0e0e0",
            borderRadius: "16px",
            padding: "32px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "24px",
              paddingBottom: "16px",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "16px",
                color: "white",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              AI
            </div>
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#333",
                margin: 0,
              }}
            >
              Streaming Response
            </h2>
          </div>

          {/* Content */}
          <StreamingTextBox text={displayedText} showCursor={showCursor} />

          {/* Controls */}
          <div
            style={{
              marginTop: "24px",
              paddingTop: "16px",
              borderTop: "1px solid #f0f0f0",
              textAlign: "center",
            }}
          >
            <button
              onClick={startStreaming}
              disabled={isStreaming}
              onMouseEnter={() => handleMouseEnter("start")}
              onMouseLeave={() => handleMouseLeave("start")}
              style={{
                background: isStreaming
                  ? "#ccc"
                  : buttonHovers.start
                    ? "#5a67d8"
                    : "#667eea",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                fontSize: "14px",
                cursor: isStreaming ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                margin: "0 8px",
                transform:
                  buttonHovers.start && !isStreaming
                    ? "translateY(-1px)"
                    : "translateY(0)",
              }}
            >
              {getButtonText()}
            </button>

            <button
              onClick={pauseStreaming}
              disabled={!isStreaming}
              onMouseEnter={() => handleMouseEnter("pause")}
              onMouseLeave={() => handleMouseLeave("pause")}
              style={{
                background: !isStreaming
                  ? "#ccc"
                  : buttonHovers.pause
                    ? "#d97706"
                    : "#f59e0b",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                fontSize: "14px",
                cursor: !isStreaming ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                margin: "0 8px",
                transform:
                  buttonHovers.pause && isStreaming
                    ? "translateY(-1px)"
                    : "translateY(0)",
              }}
            >
              Pause
            </button>

            <button
              onClick={resetText}
              onMouseEnter={() => handleMouseEnter("reset")}
              onMouseLeave={() => handleMouseLeave("reset")}
              style={{
                background: buttonHovers.reset ? "#4b5563" : "#6b7280",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                fontSize: "14px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                margin: "0 8px",
                transform: buttonHovers.reset
                  ? "translateY(-1px)"
                  : "translateY(0)",
              }}
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StreamingTextBox;
